import mixpanel from 'mixpanel-browser';
import { d, getNumber, invokeEvent, setNumber, ts } from 'shared-base';
import hashedEmails from './emails-out.json';
import { setFirstVisit, setIsEmployee, setIsPaying, setSelectors, setStore } from './globals';
import { hashSHA256 } from './hash';
import { setEnabled } from './track';
import { InitOptions } from './types';

let isEnabled = false;

/**
 * Initializes Mixpanel tracking.
 * @param mixpanelToken - The Mixpanel project token.
 * @param options - Configuration options.
 */
export const init = (mixpanelToken: string, options: InitOptions): void => {
  const { enabled, store, selectors } = options;

  if (!enabled) {
    isEnabled = false;
    return;
  }

  if (!mixpanelToken) {
    console.warn('Mixpanel initialization failed: Missing token.');
    return;
  }

  try {
    mixpanel.init(mixpanelToken);
    isEnabled = true;
    setEnabled(true);
    setSelectors(selectors);
    setStore(store);
    const hasOptOut = mixpanel.has_opted_out_tracking();
    console.log('hasOptOut =>', hasOptOut);
  } catch (error) {
    console.error('Error initializing Mixpanel:', error);
  }
};

const optOut = (email: string) => {
  isEnabled = false;
  setEnabled(false);
  mixpanel.people.set({ $email: email, $company: 'Sloyd', v2: true });
  setIsEmployee(true);
  mixpanel.opt_out_tracking();
};

/**
 * Identifies a user in Mixpanel.
 * @param email - The user's email.
 */

type IdentifyParams = {
  sub: string;
  email: string;
  isPaying: boolean;
};

const now = ts();
let lastVisitTs = getNumber('lastVisitTs');

if (!lastVisitTs) {
  setNumber('lastVisitTs', now);
}

const firstVisit = d.isSameDay(now, lastVisitTs || now);
console.log('firstVisit =>', firstVisit);
setFirstVisit(firstVisit);

export const identify = async (params: IdentifyParams) => {
  const { email, sub, isPaying } = params;

  if (!isEnabled) return;
  if (!email) {
    console.warn('Mixpanel identify failed: Email is required.');
    return;
  }

  try {
    mixpanel.identify(sub);

    if (email.endsWith('@sloyd.ai')) {
      optOut(sub);
      setIsPaying(false);
      return;
    }

    mixpanel.people.set({
      $email: email,
      v2: true,
      isPaying,
      returning: !firstVisit,
    });

    // is an employee private email?
    const hashed = await hashSHA256(email);
    const included = hashedEmails.includes(hashed);

    if (included) {
      optOut(sub);
    }

    setIsPaying(isPaying);
  } catch (error) {
    invokeEvent('senry', { error });
    console.error('Error identifying user in Mixpanel:', error);
  }
};

export const patchPersona = (change: Json) => {
  mixpanel.people.set(change);
};
